export const CLASSIC_COLUMN_COUNT = 12
export const CLASSIC_VISIBLE_ROW_COUNT = 15
export const CLASSIC_ROW_BUFFER = 3
export const CLASSIC_ROW_COUNT = CLASSIC_VISIBLE_ROW_COUNT + CLASSIC_ROW_BUFFER * 2
export const MAX_CLASSIC_ACTIVE_TARGETS = 5
export const CLASSIC_LOCK_WINDOW_MS = 10_000
export const CLASSIC_MULTIPLIER_FACTOR = 1
export const CLASSIC_PRICE_STEP_USD = 20
export const CLASSIC_PRACTICE_STAKE = 10

export type GameMode = 'deep-rush' | 'classic-swim' | 'whale-raid'
export type MarketDirection = 'up' | 'range' | 'down'
export type RoundStatus = 'selecting' | 'locked' | 'resolved' | 'void'
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary'

export interface MarketTick {
  symbol: 'BTC-USD'
  price: number
  sourceTimestamp: number
  receivedAt: number
  sequence: number
  source: 'coinbase' | 'binance' | 'okx' | 'simulated'
}

export interface PriceBand {
  index: number
  min: number
  max: number
  center: number
  multiplier: number
  direction: MarketDirection
}

export interface ClassicTile extends PriceBand {
  id: string
  column: number
  resolveAt: number
  secondsAhead: number
}

export interface BetSettlementTarget {
  kind: 'deep-rush' | 'classic-swim'
  resolveAt: number
  entryPrice: number
  bandSize: number
  min: number
  max: number
  center: number
  direction: MarketDirection
}

export interface RoundConfig {
  id: string
  mode: Exclude<GameMode, 'whale-raid'>
  symbol: 'BTC-USD'
  startedAt: number
  lockAt: number
  resolveAt: number
  entryPrice: number
  bandSize: number
  bands: PriceBand[]
  rushRules?: DeepRushRoundRules
  classicTiles?: ClassicTile[]
  classicRules?: ClassicRoundRules
}

export interface DeepRushRoundRules {
  roundMs: number
  lockMs: number
}

export interface ClassicRoundRules {
  lockWindowMs: number
  maxActiveTargets: number
  multiplierFactor: number
  priceStep: number
}

export interface PlayerPrediction {
  roundId: string
  userId: string
  betId?: string
  stake?: string
  bandIndex?: number
  tileId?: string
  selectedAt: number
  lockedMultiplier?: number
  shift?: -1 | 1
  shiftedAt?: number
}

export interface CollectionEntry {
  speciesId: string
  count: number
  firstCaughtAt: number
  bestMultiplier: number
}

export interface SpeciesDefinition {
  id: string
  name: string
  rarity: Rarity
  behavior: string
  insight: string
  color: string
  boss?: boolean
}

export interface RoundResult {
  roundId: string
  status: 'resolved' | 'void'
  success: boolean
  entryPrice: number
  resolvePrice?: number
  selectedBand?: PriceBand
  selectedTile?: ClassicTile
  multiplier: number
  xpAwarded: number
  streak: number
  frenzy: boolean
  loot?: SpeciesDefinition
  insight: string
  resolvedAt?: number
  profit?: number
  settlement?: BetSettlement
  reason?: 'market-stale' | 'no-prediction' | 'market-gap'
}

export interface RoundState extends RoundConfig {
  status: RoundStatus
  prediction?: PlayerPrediction
  rushPredictions?: PlayerPrediction[]
  rushResults?: RoundResult[]
  classicPredictions?: PlayerPrediction[]
  classicResults?: RoundResult[]
  shiftUsed: boolean
  result?: RoundResult
}

export interface AccessibilitySettings {
  masterVolume: number
  musicVolume: number
  sfxVolume: number
  reducedMotion: boolean
  highContrast: boolean
  colorAssist: boolean
}

export interface PlayerProfile {
  id: string
  callsign: string
  xp: number
  streak: number
  bestStreak: number
  roundsPlayed: number
  collection: Record<string, CollectionEntry>
  settings: AccessibilitySettings
  createdAt: number
}

export interface WalletSnapshot {
  usd: { available: string; locked: string }
  swim: { available: string }
  version: number
  updatedAt: number
}

export interface PlayerSession {
  authenticated: true
  user: {
    id: string
    name: string
    email: string
    emailVerified: boolean
    image?: string | null
    role: string
    mustChangePassword: boolean
  }
  profile: PlayerProfile
  wallet: WalletSnapshot
}

export interface BetPosition {
  id: string
  roundId: string
  mode: Exclude<GameMode, 'whale-raid'>
  targetId: string
  stake: string
  multiplier: number
  possiblePayout: string
  status: 'open' | 'won' | 'lost' | 'void'
  payout: string
  pnl?: string
  createdAt: number
  resolvedAt?: number
}

export interface JackpotAward {
  type: 'cash' | 'swim'
  amount: string
  roll: number
  thresholdPpm: number
}

export interface PoolSnapshot {
  cash: {
    amount: string
    contributionBps: number
    winRatePpm: number
  }
  weeklySwim: {
    remaining: string
    budget: string
    award: string
    winRatePpm: number
    periodKey: string
    resetAt: number
  }
  raid: {
    available: string
    projectedReward: string
    dailyTopUp: string
    weeklyCap: string
    periodKey: string
  }
}

export interface BetSettlement extends BetPosition {
  userId: string
  status: 'won' | 'lost' | 'void'
  pnl: string
  swimVolumeReward: string
  jackpots: JackpotAward[]
}

export interface ReferralProgress {
  code: string
  qualifyingVolume: string
  targetVolume: string
  qualified: boolean
  rewarded: boolean
  referrerReward: string
  refereeReward: string
}

export interface SocialQuest {
  id: string
  title: string
  description: string
  instructions: string
  rewardSwim: string
  startsAt: number
  endsAt: number
  claimStatus?: 'pending' | 'approved' | 'rejected'
}

export interface QuestClaim {
  id: string
  questId: string
  userId: string
  proofUrl: string
  note: string
  status: 'pending' | 'approved' | 'rejected'
  reviewerNote?: string
  createdAt: number
  reviewedAt?: number
}

export interface RaidParticipant {
  userId: string
  callsign: string
  contribution: number
  correct: number
  predictions: Partial<Record<0 | 1 | 2, MarketDirection>>
  joinedAt: number
}

export interface RaidCheckpoint {
  index: 0 | 1 | 2
  resolveAt: number
  phaseEntryPrice?: number
  resolvePrice?: number
  outcome?: MarketDirection
  resolved: boolean
  void: boolean
}

export interface RaidState {
  id: string
  phase: 'cooldown' | 'check-in' | 'active' | 'resolved'
  checkInAt: number
  startsAt: number
  endsAt: number
  bossHp: number
  bossMaxHp: number
  participantCount: number
  joined: boolean
  contribution: number
  correct: number
  predictionCount: number
  entryFee: string
  minimumSwim: string
  rewardPool: string
  reservedReward: string
  projectedReward: string
  checkpoints: RaidCheckpoint[]
  defeated: boolean
}

export interface RaidReward {
  raidId: string
  userId: string
  amount: string
  contribution: number
  accuracy: number
  accuracyBonus: boolean
}

export interface AdminUserSummary {
  id: string
  name: string
  email: string
  role: string
  emailVerified: boolean
  banned: boolean
  mustChangePassword: boolean
  adminCreated: boolean
  callsign?: string
  usdAvailable: string
  usdLocked: string
  swimAvailable: string
  createdAt: number
}

export interface AdminLedgerEntry {
  id: string
  scope: 'wallet' | 'pool'
  owner: string
  asset: string
  availableDelta: string
  lockedDelta: string
  type: string
  reason?: string
  actorUserId?: string
  createdAt: number
}

export interface AdminPoolSummary {
  id: string
  type: string
  periodKey: string
  available: string
  locked: string
  minted: string
  updatedAt: number
}

export interface AdminEconomyMetric {
  dayKey: string
  wagerVolume: string
  losingStakes: string
  winningProfit: string
  cashContribution: string
  ngr: string
  emaNgr: string
  raidTopUp: string
  topUpAppliedAt?: number
}

export interface AdminEconomySetting {
  key: string
  envKey: string
  value: number
  source: 'database' | 'env' | 'default'
}

export interface AdminReferralSummary {
  id: string
  referrerEmail: string
  referredEmail: string
  qualifyingVolume: string
  qualified: boolean
  rewarded: boolean
  createdAt: number
}

export interface AdminDashboard {
  users: AdminUserSummary[]
  ledger: AdminLedgerEntry[]
  pools: AdminPoolSummary[]
  metrics: AdminEconomyMetric[]
  settings: AdminEconomySetting[]
  referrals: AdminReferralSummary[]
  questClaims: QuestClaim[]
}

export type ServerEvent =
  | { type: 'market.tick'; payload: MarketTick }
  | { type: 'market.history'; payload: MarketTick[] }
  | { type: 'market.status'; payload: { healthy: boolean; message: string } }
  | { type: 'round.updated'; payload: RoundState }
  | { type: 'round.resolved'; payload: RoundResult }
  | { type: 'raid.updated'; payload: RaidState }
  | { type: 'raid.checkpoint'; payload: RaidState }
  | { type: 'raid.reward-updated'; payload: RaidReward[] }
  | { type: 'wallet.updated'; payload: WalletSnapshot }
  | { type: 'positions.updated'; payload: BetPosition[] }
  | { type: 'bet.settled'; payload: BetSettlement }
  | { type: 'pool.updated'; payload: PoolSnapshot }
  | { type: 'jackpot.won'; payload: JackpotAward & { betId: string } }

export interface ApiErrorPayload {
  error: string
  code: string
  details?: unknown
}
